import React from 'react';
import { Button, Input, InputNumber, Tag, Tooltip, Typography } from 'antd';
import { ApiOutlined, PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import type { SettingsController } from '../../hooks/useSettings';
import TargetHealthPanel from '../TargetHealthPanel';
import { CardHint, Field, SaveButton, SettingsCard, CONTROL_WIDTH } from './parts';

const { Text } = Typography;

// One line per target: the URL takes the space left over, the expected content
// sits beside it rather than on a second row of its own.
export const ProxyCheckerCard: React.FC<{ s: SettingsController }> = ({ s }) => {
  const update = (idx: number, patch: { url?: string; expectedContent?: string }) => {
    const next = [...s.proxyTargetConfigs];
    next[idx] = { ...next[idx], ...patch };
    s.setProxyTargetConfigs(next);
  };

  return (
    <SettingsCard
      title="Proxy Checker"
      icon={<ApiOutlined />}
      loading={s.loading}
      extra={<SaveButton onClick={s.handleSaveProxy} loading={s.savingProxy} />}
    >
      <CardHint>
        Configure proxy health check behavior. Proxies are tested in 3 steps: TCP connection to proxy,
        HTTP request through proxy to the target URLs (cascade), and tunnel latency measurement
        (CONNECT/SOCKS5 handshake to the successful target). Targets are tried in order — first
        successful response confirms the proxy works.
      </CardHint>

      <Text style={{ fontSize: 13 }}>Target URLs</Text>
      <CardHint>
        Requested through the proxy in order, first success wins. Max 10 targets. Expected content is
        optional: a substring that must appear in the response body to confirm the proxy returns real
        content and not a provider stub page.
      </CardHint>

      {s.proxyTargetConfigs.map((config, idx) => (
        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
          <Tag style={{ minWidth: 22, textAlign: 'center', marginInlineEnd: 0 }}>{idx + 1}</Tag>
          <Input
            size="small"
            value={config.url}
            onChange={(e) => update(idx, { url: e.target.value })}
            placeholder="https://www.google.com"
            style={{ flex: 2, minWidth: 0 }}
          />
          <Tooltip title="Expected content — a substring that must appear in the response body (optional)">
            <Input
              size="small"
              value={config.expectedContent}
              onChange={(e) => update(idx, { expectedContent: e.target.value })}
              placeholder="expected content (optional)"
              style={{ flex: 1, minWidth: 0 }}
            />
          </Tooltip>
          <Button
            type="text"
            size="small"
            danger
            disabled={s.proxyTargetConfigs.length <= 1}
            icon={<MinusCircleOutlined />}
            onClick={() => s.setProxyTargetConfigs(s.proxyTargetConfigs.filter((_, i) => i !== idx))}
          />
        </div>
      ))}
      {s.proxyTargetConfigs.length < 10 && (
        <Button
          type="dashed"
          size="small"
          icon={<PlusOutlined />}
          onClick={() => s.setProxyTargetConfigs([...s.proxyTargetConfigs, { url: '', expectedContent: '' }])}
          style={{ width: 200, marginBottom: 12 }}
        >
          Add Target
        </Button>
      )}

      <TargetHealthPanel configured={s.proxyTargetConfigs.map((c) => c.url)} />

      <div style={{ marginTop: 12 }}>
        <Field
          label="Request timeout"
          hint="Maximum time allowed for the HTTP request through proxy. If exceeded, the request step fails."
        >
          <InputNumber
            size="small"
            value={s.proxyTimeoutMs}
            onChange={(val) => s.setProxyTimeoutMs(val ?? 30000)}
            min={1000}
            max={120000}
            step={1000}
            style={{ width: CONTROL_WIDTH }}
            addonAfter="ms"
          />
        </Field>
        <Field
          label="Max tunnel latency"
          hint="Maximum acceptable tunnel establishment time (CONNECT/SOCKS5 handshake). Measures pure proxy overhead. Proxy is marked as degraded if exceeded."
        >
          <InputNumber
            size="small"
            value={s.proxyMaxLatencyMs}
            onChange={(val) => s.setProxyMaxLatencyMs(val ?? 30000)}
            min={1000}
            max={120000}
            step={1000}
            style={{ width: CONTROL_WIDTH }}
            addonAfter="ms"
          />
        </Field>
      </div>
    </SettingsCard>
  );
};

export default ProxyCheckerCard;
